import { Injectable } from '@angular/core';
import { StorageService } from './index';
import {
  Tarea,
  TareaTipo,
  TareaRol,
  Actividad,
  Const,
  TareaAsociacion,
  NameTaskType,
  Static,
  TareaCondicion,
  Documento
} from '@app/shared';
import { Logger } from '@app/core';
import { merge, groupBy, findIndex, find } from 'lodash';
import { ConfigFields } from '@app/shared/task/shared/form-service';
import { ManageActivityService } from './manage-activity.service';
import { TaskService } from '@app/shared/task/shared/task.service';

const log = new Logger('ManageTaskService');

/**
 * Administra todo lo relacionado con las tareas,
 * en la automatización de procesos.
 *
 * @export
 * @class ManageTaskService
 */
@Injectable()
export class ManageTaskService {
  // Tipos de las tareas
  types: TareaTipo[];
  // Tipos de finalización para las secundarias
  completionTypesSecondary: TareaTipo[];
  // Todos los roles del sistema compatibles con las tareas
  roles: TareaRol[] = [];
  // Contiene el id de saltar tarea
  idCompletionSkipTask = -1;

  constructor(
    protected ss: StorageService,
    private ts: TaskService,
    private mas: ManageActivityService
  ) {
    this.init();
  }

  async init() {
    // Consultar los posibles tipos de una tarea
    const types = await this.ts.getTypes();
    const indexTypeSecondary = findIndex(
      types,
      type => {
        return String(type.nombre).toLowerCase() === Const.TYPES_TASK.secondary.toLowerCase();
      }
    );
    if (indexTypeSecondary !== -1) {
      types.splice(indexTypeSecondary, 1);
    }
    // Eliminar tipos de tarea que no se muestran al usuario
    const notIncludeTypes = ['asignacion'];
    this.types = types.filter(el => {
      return !notIncludeTypes.includes(el.nombre);
    });
    // Consultar los roles del sistema y generar array de 'TareaRol'
    const rolesUsers = await this.ts.getRoles();
    rolesUsers.forEach(el => {
      this.roles.push(
        new TareaRol({
          rol_id: el.id,
          nombre: el.nombre,
          descripcion: el.descripcion
        })
      );
    });
  }

  /**
   * Detemina si una cadeana correponde al valor
   * de un tipo de tarea especifico.
   *
   * @param {number} id - id del tipo usado para comparar.
   * @param {NameTaskType} type - Tipo de tarea definido 'TYPES_TASK'.
   * @returns {boolean}
   * @memberof ManageTaskService
   */
  isType(id: number, type: NameTaskType): boolean {
    const validTypes = Const.TYPES_TASK;
    if (validTypes.hasOwnProperty(type)) {
      const _type = find(this.types, { id: Number(id) });
      if (_type) {
        return validTypes[type] === _type.nombre;
      }
    }
    return false;
  }

  /**
   * Edita la información de la tarea almacenada en una
   * determinada actividad.
   *
   * @param {Actividad} activity
   * @param {Tarea} task
   * @returns {boolean}
   * @memberof ManageTaskService
   */
  edit(activity: Actividad, task: Tarea): boolean {
    const indexOldTask = findIndex(activity.tareas, { id: task.id });
    if (indexOldTask !== -1) {
      activity.tareas[indexOldTask] = task;
      // Si la tarea es un microproceso
      if (task.tarea_tipo.nombre === Const.TYPES_TASK.microProcess) {
        this.ss.updateMicroProcess(task);
      } else {
        if (task.tarea_asociacion && task.tarea_asociacion.length) {
          task.tarea_asociacion.forEach(taskAssociation => {
            if (!taskAssociation.id_tarea_inicio) {
              taskAssociation.id_tarea_inicio = task.id;
            }
          });
        }
        if (task.tarea_finalizacion && task.tarea_finalizacion.length) {
          task.tarea_finalizacion.forEach(taskFinaization => {
            if (!taskFinaization.tarea_destino) {
              taskFinaization.tarea_destino = 0;
            }
            if (!taskFinaization.opcion) {
              taskFinaization.opcion = 0;
            }
          });
        }
        if (task.documentos && task.documentos.length) {
          task.documentos.forEach((document, index) => {
            const doc = this.purgeDocument(document);
            if (doc) {
              task.documentos[index] = doc;
            }
          });
        }
        if (task.tarea_condicion && task.tarea_condicion.length) {
          task.tarea_condicion = Static.getMapConditions(<TareaCondicion[]>task.tarea_condicion);
        }
        if (task.tarea_asociacion && task.tarea_asociacion) {
          task.tarea_asociacion.forEach((taskAssociation, index) => {
            task.tarea_asociacion[index] = this.purgeAssociation(taskAssociation);
          });
        }
      }
      // Si el flujo esta sincronizado con la DB entonces se guarda
      // esta acción en un historial.
      this.ss.registerActionInHistory('tasks', 'update', {
        activityId: activity.id,
        taskId: task.id
      }, task.id);
      if (this.mas.edit(activity)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Combina el array de roles responsables con el de asignaciones,
   * validando si se trata solo de un rol perteneciente a un grupo o ambos.
   *
   * @param {Array<Partial<TareaRol>>} assigned
   * @param {Array<Partial<TareaRol>>} responsibles
   * @memberof ManageTaskService
   */
  generateRoleFormat(assigned: Array<Partial<TareaRol>>, responsibles: Array<Partial<TareaRol>>): TareaRol[] {
    const groupAssigned: Array<Partial<TareaRol>> = [];
    const groupResponsibles: Array<Partial<TareaRol>> = [];
    // Definir si el rol pertenece al grupo de responsables o de asignaciones
    assigned.forEach(el => {
      groupAssigned.push({
        rol_id: el.rol_id,
        nombre: el.nombre,
        descripcion: el.descripcion,
        asignacion: true
      });
      groupAssigned[groupAssigned.length - 1]['type'] = 'assigned';
    });
    responsibles.forEach(el => {
      groupResponsibles.push({
        rol_id: el.rol_id,
        nombre: el.nombre,
        descripcion: el.descripcion,
        responsable: true
      });
      groupResponsibles[groupResponsibles.length - 1]['type'] = 'responsible';
    });
    // Concatenar todos los roles y agruparlos por 'id_rol'
    const group = groupBy([...groupAssigned, ...groupResponsibles], 'rol_id');
    const formatted = [];
    // Recorrer los grupos
    for (const key in group) {
      if (group.hasOwnProperty(key)) {
        const arr = group[key];
        let forPush;
        // Si este grupo solo contine un rol es porque no esta repetido en array
        if (group[key].length === 1) {
          forPush = arr[0];
          if (forPush['type'] === 'assigned') {
            forPush.responsable = false;
          } else {
            forPush.asignacion = false;
          }
        } else {
          // Este rol pertenece a ambos grupos(responsables o de asignaciones).
          forPush = merge(arr[0], arr[1]);
        }
        delete forPush['type'];
        formatted.push(forPush);
      }
    }
    return formatted as TareaRol[];
  }

  /**
   * Genera un array de roles para los responsables
   * y los encargados de asignar.
   *
   * @param {TareaRol[]} roles
   * @memberof ManageTaskService
   */
  destructureRoles(roles: TareaRol[]) {
    const assigned: Partial<TareaRol[]> = [],
      responsibles: Partial<TareaRol[]> = [];
    roles.forEach(el => {
      if (el.asignacion) {
        assigned.push(el);
      }
      if (el.responsable) {
        responsibles.push(el);
      }
    });
    return {
      assigned: assigned,
      responsibles: responsibles
    };
  }

  /**
   * Retorna los controles necesarios para crear un formulario dinámico.
   *
   * @param {Partial<ConfigFieldsActivity>} config
   * @returns
   * @memberof ManageTaskService
   */
  getControlsForDynamicForm(config: Partial<ConfigFields>) {
    return this.ts.getControls(config, true);
  }

  /**
   * Limpia el objeto de la tarea asociada de las claves innesesarias para enviar al backend
   *
   * @private
   * @param {TareaAsociacion} taskAssociation
   * @returns {*}
   * @memberof ManageTaskService
   */
  private purgeAssociation(taskAssociation: TareaAsociacion): any {
    let newTaskAssociation: any;
    newTaskAssociation = {
      alias: taskAssociation.alias,
      id_tarea_destino: taskAssociation.id_tarea_destino,
      id_tarea_inicio: taskAssociation.id_tarea_inicio,
      id_tipo_asociacion: taskAssociation.id_tipo_asociacion,
      tipo_asociacion: taskAssociation.tipo_asociacion,
      documentos: [],
    };
    if (taskAssociation['task']) {
      newTaskAssociation['task'] = taskAssociation['task'];
    }
    if (taskAssociation['proceso_externo']) {
      newTaskAssociation['proceso_externo'] = taskAssociation.proceso_externo;
    }
    if (taskAssociation.documentos && taskAssociation.documentos.length) {
      taskAssociation.documentos.map(document => {
        const doc = this.purgeDocument(document, true);
        if (doc) {
          newTaskAssociation.documentos.push(doc);
        }
      });
    }
    return newTaskAssociation;
  }

  /**
   * Limpia el objeto de los documentos de la tarea de claves innesesarias para enviar al backend
   *
   * @private
   * @param {Documento} document
   * @param {boolean} [association=false]
   * @returns {*}
   * @memberof ManageTaskService
   */
  private purgeDocument(document: Documento, association: boolean = false): any {
    let newDocument = null;
    let idDocument = -1;
    if (association) {
      if (!document['id_documento']) {
        idDocument = document.documento;
      } else {
        idDocument = document['id_documento'];
      }
      if (idDocument !== -1) {
        newDocument = {
          id_documento: idDocument,
          funciones: document.funciones
        };
      }
    } else {
      idDocument = document.documento;
      if (idDocument !== -1) {
        newDocument = {
          clasificacion: document.clasificacion,
          tipo_documental: document.tipo_documental,
          documento: idDocument,
          funciones: document.funciones
        };
      }
    }
    return newDocument;
  }
} // End clas



// WEBPACK FOOTER //
// ./src/app/administration/basic-process-configuration/shared/manage-task.service.ts